// src/components/AnnouncementForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, doc, getDoc, addDoc, updateDoc, serverTimestamp, query, where, orderBy, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faBullhorn,
    faSave,
    faPaperPlane,
    faCheck,
    faExclamationCircle,
    faAlignLeft,
    faUsers,
    faBell,
    faEnvelope,
    faChevronDown,
    faChevronUp,
    faEye,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

import './FormPage.css';

function AnnouncementForm() {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [targeting, setTargeting] = useState('all');
    const [channelPush, setChannelPush] = useState(true);
    const [channelEmail, setChannelEmail] = useState(false);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [selectedFamilyIds, setSelectedFamilyIds] = useState([]);
    const [groups, setGroups] = useState([]);
    const [families, setFamilies] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isFetching, setIsFetching] = useState(isEditMode);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [showRecipientList, setShowRecipientList] = useState(false);
    const previewTimerRef = useRef(null);
    const navigate = useNavigate();
    const { currentUser, userOrgId, isAdmin } = useAuth();

    // Fetch groups and families for the organization
    useEffect(() => {
        if (!userOrgId) return;
        const fetchData = async () => {
            const [groupsSnapshot, familiesSnapshot] = await Promise.all([
                getDocs(query(
                    collection(db, 'group'),
                    where('orgId', '==', userOrgId),
                    orderBy('groupName')
                )),
                getDocs(query(
                    collection(db, 'family'),
                    where('orgId', '==', userOrgId),
                    orderBy('surname')
                ))
            ]);
            setGroups(groupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setFamilies(familiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };
        fetchData();
    }, [userOrgId]);

    // Fetch existing announcement for edit mode
    useEffect(() => {
        if (!isEditMode || !currentUser || !isAdmin) return;
        const fetchAnnouncement = async () => {
            setIsFetching(true);
            try {
                const announcementRef = doc(db, 'communications', id);
                const announcementSnap = await getDoc(announcementRef);
                if (!announcementSnap.exists()) {
                    setError('Announcement not found.');
                    return;
                }
                const data = announcementSnap.data();
                setTitle(data.title || '');
                setBody(data.body || '');
                setTargeting(data.targeting?.type || 'all');
                setSelectedGroupIds(data.targeting?.groupIds || []);
                setSelectedFamilyIds(data.targeting?.familyIds || []);
                setChannelPush(data.channels?.push ?? true);
                setChannelEmail(data.channels?.email ?? false);
            } catch (err) {
                console.error('Error fetching announcement:', err);
                setError(err.message || 'Failed to load announcement.');
            } finally {
                setIsFetching(false);
            }
        };
        fetchAnnouncement();
    }, [id, isEditMode, currentUser, isAdmin]);

    // Debounced recipient preview
    const fetchPreview = useCallback(async (targetingConfig, channelsConfig) => {
        // Don't preview if no channels selected
        if (!channelsConfig.push && !channelsConfig.email) {
            setPreview(null);
            return;
        }
        // Don't preview if groups targeting with no groups selected
        if (targetingConfig.type === 'groups' && (!targetingConfig.groupIds || targetingConfig.groupIds.length === 0)) {
            setPreview(null);
            return;
        }
        // Don't preview if families targeting with no families selected
        if (targetingConfig.type === 'families' && (!targetingConfig.familyIds || targetingConfig.familyIds.length === 0)) {
            setPreview(null);
            return;
        }

        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const functions = getFunctions();
            const previewRecipientsFn = httpsCallable(functions, 'previewRecipients');
            const result = await previewRecipientsFn({
                targeting: targetingConfig,
                channels: channelsConfig
            });
            setPreview(result.data);
        } catch (err) {
            console.error('Preview error:', err);
            setPreviewError('Could not load recipient preview.');
            setPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    }, []);

    useEffect(() => {
        if (previewTimerRef.current) {
            clearTimeout(previewTimerRef.current);
        }

        const targetingConfig = {
            type: targeting,
            groupIds: targeting === 'groups' ? selectedGroupIds : [],
            familyIds: targeting === 'families' ? selectedFamilyIds : []
        };
        const channelsConfig = { push: channelPush, email: channelEmail };

        previewTimerRef.current = setTimeout(() => {
            fetchPreview(targetingConfig, channelsConfig);
        }, 500);

        return () => {
            if (previewTimerRef.current) {
                clearTimeout(previewTimerRef.current);
            }
        };
    }, [targeting, selectedGroupIds, selectedFamilyIds, channelPush, channelEmail, fetchPreview]);

    const validateForm = () => {
        setError(null);
        if (!title.trim()) {
            setError("Announcement title cannot be empty.");
            return false;
        }
        if (!body.trim()) {
            setError("Announcement body cannot be empty.");
            return false;
        }
        if (!channelPush && !channelEmail) {
            setError("Please select at least one channel (Push or Email).");
            return false;
        }
        if (targeting === 'groups' && selectedGroupIds.length === 0) {
            setError("Please select at least one group.");
            return false;
        }
        if (targeting === 'families' && selectedFamilyIds.length === 0) {
            setError("Please select at least one family.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (event, sendNow = false) => {
        event.preventDefault();
        if (!validateForm() || !currentUser || !isAdmin || !userOrgId) {
            if (!currentUser) setError("You must be logged in to create an announcement.");
            if (!isAdmin) setError("You must be a admin to create announcements.");
            return;
        }

        if (sendNow) {
            setIsSending(true);
        } else {
            setIsLoading(true);
        }
        setError(null);
        setSuccessMessage(null);

        try {
            const announcementData = {
                title: title.trim(),
                body: body.trim(),
                targeting: {
                    type: targeting,
                    groupIds: targeting === 'groups' ? selectedGroupIds : [],
                    familyIds: targeting === 'families' ? selectedFamilyIds : []
                },
                channels: {
                    push: channelPush,
                    email: channelEmail
                },
                status: 'draft',
                orgId: userOrgId,
                createdBy: currentUser.uid,
                createdAt: serverTimestamp()
            };

            let announcementId;

            if (isEditMode) {
                // Update existing announcement
                const announcementRef = doc(db, 'communications', id);
                await updateDoc(announcementRef, {
                    ...announcementData,
                    updatedAt: serverTimestamp()
                });
                announcementId = id;
            } else {
                // Create new announcement
                const docRef = await addDoc(collection(db, 'communications'), announcementData);
                announcementId = docRef.id;
            }

            // If "Send Now" was clicked, call sendAnnouncement function (has side effects)
            if (sendNow && announcementId) {
                const functions = getFunctions();
                const sendAnnouncement = httpsCallable(functions, 'sendAnnouncement');
                await sendAnnouncement({ announcementId });
                setSuccessMessage(isEditMode ? "Announcement updated and sent!" : "Announcement sent successfully!");
            } else {
                setSuccessMessage(isEditMode ? "Announcement updated!" : "Announcement saved as draft!");
            }

            setTimeout(() => {
                navigate('/communications', { state: { message: isEditMode ? 'Announcement updated.' : (sendNow ? 'Announcement sent.' : 'Announcement saved.') } });
            }, 1500);
        } catch (err) {
            console.error("Error creating announcement:", err);
            setError(err.message || "Failed to create announcement. Please try again.");
        } finally {
            setIsLoading(false);
            setIsSending(false);
        }
    };

    // Redirect non-super-admins
    if (!isAdmin) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/communications" className="form-back-btn" title="Back to Communications">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Access Denied</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-card">
                        <div className="form-card-body">
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">
                                    Only administrators can create announcements.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Loading skeleton for edit mode
    if (isFetching) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/communications" className="form-back-btn" title="Back to Communications">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Loading...</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-loading">
                        <div className="skeleton-header"></div>
                        <div className="skeleton-form"></div>
                    </div>
                </div>
            </div>
        );
    }

    const formInputsDisabled = isLoading || isSending || !!successMessage;

    return (
        <div className="form-page">
            {/* Page Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to="/communications" className="form-back-btn" title="Back to Communications">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">{isEditMode ? 'Edit Announcement' : 'Create Announcement'}</h1>
                                <p className="form-page-subtitle">{isEditMode ? 'Update announcement details' : 'Send a message to your organization'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Form Card */}
                <div className="form-card">
                    <div className="form-card-body">
                        {/* Error Alert */}
                        {error && (
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">{error}</div>
                            </div>
                        )}

                        {/* Success Alert */}
                        {successMessage && (
                            <div className="form-alert form-alert-success">
                                <FontAwesomeIcon icon={faCheck} className="form-alert-icon" />
                                <div className="form-alert-content">{successMessage}</div>
                            </div>
                        )}

                        {!successMessage && (
                            <form onSubmit={(e) => handleSubmit(e, false)} noValidate>
                                {/* Announcement Content Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: '0.5rem' }} />
                                        Announcement Content
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Title <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-field-input"
                                            placeholder="Enter announcement title"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            disabled={formInputsDisabled}
                                            maxLength={200}
                                            autoFocus
                                        />
                                        <div style={{ textAlign: 'right', fontSize: '0.8rem', color: title.length > 180 ? '#dc2626' : '#94a3b8', marginTop: '0.25rem' }}>
                                            {title.length}/200
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            <FontAwesomeIcon icon={faAlignLeft} style={{ marginRight: '0.5rem' }} />
                                            Body <span className="required">*</span>
                                        </label>
                                        <textarea
                                            className="form-field-input form-field-textarea"
                                            placeholder="Enter announcement message"
                                            value={body}
                                            onChange={(e) => setBody(e.target.value)}
                                            disabled={formInputsDisabled}
                                            rows={5}
                                            maxLength={5000}
                                        />
                                        <div style={{ textAlign: 'right', fontSize: '0.8rem', color: body.length > 4500 ? '#dc2626' : '#94a3b8', marginTop: '0.25rem' }}>
                                            {body.length}/5000
                                        </div>
                                    </div>
                                </div>

                                {/* Targeting Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.5rem' }} />
                                        Recipients
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Send To <span className="required">*</span>
                                        </label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="targeting"
                                                    value="all"
                                                    checked={targeting === 'all'}
                                                    onChange={(e) => { setTargeting(e.target.value); setSelectedGroupIds([]); setSelectedFamilyIds([]); }}
                                                    disabled={formInputsDisabled}
                                                />
                                                All Members
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="targeting"
                                                    value="groups"
                                                    checked={targeting === 'groups'}
                                                    onChange={(e) => setTargeting(e.target.value)}
                                                    disabled={formInputsDisabled}
                                                />
                                                Select Groups
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="targeting"
                                                    value="families"
                                                    checked={targeting === 'families'}
                                                    onChange={(e) => setTargeting(e.target.value)}
                                                    disabled={formInputsDisabled}
                                                />
                                                Select Families
                                            </label>
                                        </div>

                                        {targeting === 'groups' && (
                                            <div style={{ marginTop: '0.75rem', paddingLeft: '1.5rem' }}>
                                                {groups.length === 0 ? (
                                                    <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>No groups found for this organization.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                                        {groups.map(group => (
                                                            <label key={group.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedGroupIds.includes(group.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedGroupIds(prev => [...prev, group.id]);
                                                                        } else {
                                                                            setSelectedGroupIds(prev => prev.filter(id => id !== group.id));
                                                                        }
                                                                    }}
                                                                    disabled={formInputsDisabled}
                                                                />
                                                                {group.groupName}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {targeting === 'families' && (
                                            <div style={{ marginTop: '0.75rem', paddingLeft: '1.5rem' }}>
                                                {families.length === 0 ? (
                                                    <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>No families found for this organization.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '250px', overflowY: 'auto' }}>
                                                        {families.map(family => (
                                                            <label key={family.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedFamilyIds.includes(family.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedFamilyIds(prev => [...prev, family.id]);
                                                                        } else {
                                                                            setSelectedFamilyIds(prev => prev.filter(id => id !== family.id));
                                                                        }
                                                                    }}
                                                                    disabled={formInputsDisabled}
                                                                />
                                                                {family.surname}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Recipient Preview */}
                                {(channelPush || channelEmail) && (targeting === 'all' || (targeting === 'groups' && selectedGroupIds.length > 0) || (targeting === 'families' && selectedFamilyIds.length > 0)) && (
                                    <div className="recipient-preview">
                                        <h3 className="form-section-title">
                                            <FontAwesomeIcon icon={faEye} style={{ marginRight: '0.5rem' }} />
                                            Recipient Preview
                                        </h3>

                                        {previewLoading && (
                                            <div className="recipient-summary" style={{ justifyContent: 'center' }}>
                                                <Spinner animation="border" size="sm" /> Resolving recipients...
                                            </div>
                                        )}

                                        {previewError && (
                                            <div className="form-alert form-alert-warning" style={{ marginBottom: 0 }}>
                                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                                <div className="form-alert-content">{previewError}</div>
                                            </div>
                                        )}

                                        {preview && !previewLoading && (
                                            <>
                                                <div className="recipient-summary">
                                                    <span><strong>{preview.summary.total}</strong> recipient{preview.summary.total !== 1 ? 's' : ''}</span>
                                                    <span className="recipient-summary-divider">|</span>
                                                    {channelPush && (
                                                        <span className="channel-badge channel-badge-push">
                                                            <FontAwesomeIcon icon={faBell} /> {preview.summary.push} push
                                                        </span>
                                                    )}
                                                    {channelEmail && (
                                                        <span className="channel-badge channel-badge-email">
                                                            <FontAwesomeIcon icon={faEnvelope} /> {preview.summary.email} email
                                                        </span>
                                                    )}
                                                    {preview.summary.unreachable > 0 && (
                                                        <span className="channel-badge channel-badge-unreachable">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} /> {preview.summary.unreachable} unreachable
                                                        </span>
                                                    )}
                                                </div>

                                                <button
                                                    type="button"
                                                    className="recipient-toggle-btn"
                                                    onClick={() => setShowRecipientList(!showRecipientList)}
                                                >
                                                    <FontAwesomeIcon icon={showRecipientList ? faChevronUp : faChevronDown} />
                                                    {showRecipientList ? 'Hide' : 'View'} recipient list
                                                </button>

                                                {showRecipientList && (
                                                    <div className="recipient-list">
                                                        {preview.recipients.map((r, idx) => (
                                                            <div key={idx} className="recipient-row">
                                                                <span className="recipient-name">{r.surname}, {r.name}</span>
                                                                <span className="recipient-channels">
                                                                    <span className={`channel-indicator ${r.hasPush ? 'channel-yes' : 'channel-no'}`} title={r.hasPush ? 'Has push' : 'No push'}>
                                                                        <FontAwesomeIcon icon={faBell} />
                                                                    </span>
                                                                    <span className={`channel-indicator ${r.hasEmail ? 'channel-yes' : 'channel-no'}`} title={r.hasEmail ? 'Has email' : 'No email'}>
                                                                        <FontAwesomeIcon icon={faEnvelope} />
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Channels Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.5rem' }} />
                                        Delivery Channels
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Select Channels <span className="required">*</span>
                                        </label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={channelPush}
                                                    onChange={(e) => setChannelPush(e.target.checked)}
                                                    disabled={formInputsDisabled}
                                                />
                                                <FontAwesomeIcon icon={faBell} style={{ color: '#3b82f6' }} />
                                                Push Notification
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={channelEmail}
                                                    onChange={(e) => setChannelEmail(e.target.checked)}
                                                    disabled={formInputsDisabled}
                                                />
                                                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#10b981' }} />
                                                Email
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="form-actions" style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                                    <button
                                        type="submit"
                                        className="form-submit-btn"
                                        disabled={formInputsDisabled}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSave} />
                                                Save as Draft
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="form-submit-btn form-submit-btn-success"
                                        disabled={formInputsDisabled}
                                        onClick={(e) => handleSubmit(e, true)}
                                    >
                                        {isSending ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faPaperPlane} />
                                                Send Now
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                        {successMessage && (
                            <div className="form-actions" style={{ borderTop: 'none', paddingTop: 0 }}>
                                <button
                                    type="button"
                                    className="form-submit-btn"
                                    onClick={() => navigate('/communications')}
                                >
                                    <FontAwesomeIcon icon={faBullhorn} />
                                    Go to Communications
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AnnouncementForm;
